import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, RotateCcw, RefreshCw, AlertTriangle, ExternalLink, User, Truck, Download } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import {
  useValidationEnAttente, useValiderCommande, useRenvoyerCommande, useATraiter,
} from '@/hooks/useCommercial';
import { formatFCFA } from '@/utils/currency';
import { useBcaEnAttente, useValiderBca, useRenvoyerBca } from '@/hooks/useAchats';
import { commercialApi, type Proforma } from '@/api/commercial';
import { achatsApi, type Bca } from '@/api/achats';
import { toast } from '@/stores/toastStore';
import { demander } from '@/components/ui/Fenetres';
import { useValidateInvoice } from '@/hooks/useInvoices';
import { extractError } from '@/api/client';
import { Badge, Modal, ModalHeader } from '@/pages/commercial/_ui';
import { useDeclarationsSoumises, useValiderDeclaration } from '@/hooks/useTerrainAdmin';
import { DeclarationValidationCard, RenvoyerDeclarationDialog } from '@/pages/terrain/DeclarationValidationCard';

/**
 * SIMG-COMMERCIAL-LEADBETON-2026-001 — Écran DAF unifié « Validations en attente ».
 *
 * Vue centralisée : le DAF voit tout ce qu'il doit valider, tous départements
 * confondus — proformas, bons de commande fournisseur, et depuis le 21/09/2026
 * les déclarations du terrain.
 *
 * Ces dernières avaient leur propre écran, « Validations DAF », sous le Portail
 * Terrain. Le DAF lisait donc deux entrées de menu au nom presque identique
 * pour un seul et même geste. Tout est ici.
 */
export const ValidationsUnifieePage = () => {
  const proformasQ = useValidationEnAttente();
  const bcaQ       = useBcaEnAttente();
  const terrainQ   = useDeclarationsSoumises();
  // TICKET-004 C-04 : toutes les files en attente du DAF (le compte décide de l'écran vide, jamais une file en panne).
  const toutesQ    = useATraiter();
  const navigate   = useNavigate();
  // TICKET-004 C-03 : valider une proforma directement depuis cette page (fenêtre de l'application, jamais confirm()).
  const validerFacture = useValidateInvoice();
  const validerProforma = async (id: string, reference: string) => {
    const ok = await demander.confirmer({
      titre: `Convertir ${reference} en facture finale ?`,
      message: '→ Nouvelle référence FACT-… générée\n→ Écriture comptable de vente passée automatiquement\n'
        + '→ Production lancée automatiquement (commande + ordres de fabrication) pour le béton ou l’impression',
      confirmer: 'Valider en facture',
    });
    if (!ok) return;
    try {
      await validerFacture.mutateAsync(id);
      toast.success('Facture validée', `${reference} est devenue une facture.`);
      void toutesQ.refetch();
    } catch (e) {
      toast.error('Validation refusée', extractError(e).message);
    }
  };
  const autresFiles = (toutesQ.data?.files ?? []).filter((f) => !['commandes', 'bca', 'terrain'].includes(f.cle));
  const [renvoyer, setRenvoyer]         = useState<Proforma | null>(null);
  const [renvoyerBcaState, setRvBca]    = useState<Bca | null>(null);
  const [renvoyerDecl, setRenvoyerDecl] = useState<any | null>(null);
  const valider        = useValiderCommande();
  const validerBca     = useValiderBca();
  const validerTerrain = useValiderDeclaration();

  const groupes = useMemo(() => ({
    commercial: proformasQ.data ?? [],
    achats:     bcaQ.data ?? [],
    terrain:    (terrainQ.data ?? []) as any[],
  }), [proformasQ.data, bcaQ.data, terrainQ.data]);

  const isLoading  = proformasQ.isLoading || bcaQ.isLoading || terrainQ.isLoading || toutesQ.isLoading;
  const isFetching = proformasQ.isFetching || bcaQ.isFetching || terrainQ.isFetching || toutesQ.isFetching;
  const refetch    = () => { void proformasQ.refetch(); void bcaQ.refetch(); void terrainQ.refetch(); void toutesQ.refetch(); };
  // Une file illisible n'est jamais comptée pour zéro : « Rien à valider » exige que tout ait été lu.
  const enPanne = Boolean(proformasQ.error || bcaQ.error || terrainQ.error || toutesQ.error) || (toutesQ.data?.indisponibles ?? 0) > 0;
  const totalEnAttente = Math.max(
    groupes.commercial.length + groupes.achats.length + groupes.terrain.length,
    toutesQ.data?.total ?? 0,
  );

  return (
    <>
      <Topbar
        title="Validations en attente"
        subtitle={`Tout ce qui attend votre accord${toutesQ.data ? ` — ${toutesQ.data.total} pièce${toutesQ.data.total > 1 ? 's' : ''}` : ''}`}
        actions={
          <button
            className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()} title="Rafraîchir"
          >
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          </button>
        }
      />
      <div className="ds-content">
        {isLoading && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>
        )}

        {!isLoading && enPanne && (
          <div className="ds-alert-strip ds-alert-w" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={20} /></span>
            <div className="ds-alert-txt">
              Une partie des files n'a pas pu être lue
              {toutesQ.data?.files.filter((f) => f.etat === 'indisponible').map((f) => ` · ${f.libelle}`).join('') ?? ''}.
              Rafraîchissez ; si cela persiste, signalez-le.
            </div>
          </div>
        )}

        {!isLoading && !enPanne && totalEnAttente === 0 && (
          <div className="ds-panel" style={{ padding: 32, textAlign: 'center' }}>
            <CheckCircle2 size={40} style={{ color: 'var(--ok-1)', marginBottom: 8 }} />
            <p style={{ color: 'var(--tx-2)', margin: 0 }}>Rien à valider — tout est à jour.</p>
          </div>
        )}

        {autresFiles.filter((f) => f.etat === 'ok' && f.nombre > 0).map((f) => (
          <section key={f.cle} style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <h2 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>{f.libelle}</h2>
              <span className="ds-bd ds-bd-w" style={{ fontSize: 11 }}>{f.nombre}</span>
              <Link to={f.lien} style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--p)' }}>Ouvrir l’écran <ExternalLink size={11} style={{ display: 'inline' }} /></Link>
            </div>
            <div className="ds-table-panel">
              <table className="ds-tbl" style={{ width: '100%' }}>
                <tbody>
                  {f.elements.map((e) => (
                    // Toute la ligne ouvre la pièce (réserve de recette C-04) ; le bouton Valider garde son propre geste.
                    <tr
                      key={e.id} tabIndex={0} style={{ cursor: 'pointer' }} title={`Ouvrir ${e.reference}`}
                      onClick={(ev) => { if (!(ev.target as HTMLElement).closest('button, a')) navigate(e.lien); }}
                      onKeyDown={(ev) => { if (ev.key === 'Enter' && ev.target === ev.currentTarget) navigate(e.lien); }}
                    >
                      <td style={{ width: 170 }}><Link to={e.lien} style={{ color: 'var(--p)', fontWeight: 600 }}>{e.reference}</Link></td>
                      <td style={{ fontSize: 12 }}>{e.libelle}</td>
                      <td style={{ fontSize: 12, width: 100 }}>{e.date ? e.date.split('-').reverse().join('/') : ''}</td>
                      <td className="ds-am" style={{ width: 140 }}>{e.montantCents ? formatFCFA(BigInt(e.montantCents)) : ''}</td>
                      {f.cle === 'proformas' && (
                        <td style={{ width: 110, textAlign: 'right' }}>
                          <button className="ds-btn ds-btn-p ds-btn-sm" disabled={validerFacture.isPending} onClick={() => void validerProforma(e.id, e.reference)}>
                            <CheckCircle2 size={12} /> Valider
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {f.nombre > f.elements.length && (
                    <tr><td colSpan={4} style={{ fontSize: 12, color: 'var(--tx-3)' }}>… et {f.nombre - f.elements.length} autre(s) dans l’écran.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        ))}

        {groupes.commercial.length > 0 && (
          <section style={{ marginBottom: 24 }}>
            <SectionHeader
              titre="Commercial · Proformas"
              count={groupes.commercial.length}
              description="Le prospect a signé, la proforma engage l'entreprise si tu la valides."
            />
            <div style={{ display: 'grid', gap: 12 }}>
              {groupes.commercial.map((p) => (
                <ProformaValidationCard
                  key={p.id}
                  proforma={p}
                  pending={valider.isPending}
                  onValider={() => valider.mutate(p.id)}
                  onRenvoyer={() => setRenvoyer(p)}
                />
              ))}
            </div>
          </section>
        )}

        {groupes.terrain.length > 0 && (
          <section style={{ marginBottom: 24 }}>
            <SectionHeader
              titre="Terrain · Déclarations de chantier"
              count={groupes.terrain.length}
              description="Ce que les équipes ont déclaré. L'écart avec le stock de l'outil est indiqué sur chaque ligne."
            />
            <div style={{ display: 'grid', gap: 12 }}>
              {groupes.terrain.map((d: any) => (
                <DeclarationValidationCard
                  key={d.id}
                  declaration={d}
                  pending={validerTerrain.isPending}
                  onValider={() => validerTerrain.mutate(d.id)}
                  onRenvoyer={() => setRenvoyerDecl(d)}
                />
              ))}
            </div>
          </section>
        )}

        {groupes.achats.length > 0 && (
          <section style={{ marginBottom: 24 }}>
            <SectionHeader
              titre="Achats · Bons commande fournisseur"
              count={groupes.achats.length}
              description="L'acheteur veut engager la filiale auprès d'un fournisseur — tu valides ou renvoies."
            />
            <div style={{ display: 'grid', gap: 12 }}>
              {groupes.achats.map((b) => (
                <BcaValidationCard
                  key={b.id}
                  bca={b}
                  pending={validerBca.isPending}
                  onValider={() => validerBca.mutate(b.id)}
                  onRenvoyer={() => setRvBca(b)}
                />
              ))}
            </div>
          </section>
        )}
      </div>

      {renvoyerDecl && (
        <RenvoyerDeclarationDialog
          declaration={renvoyerDecl}
          onClose={() => setRenvoyerDecl(null)}
          onDone={() => setRenvoyerDecl(null)}
        />
      )}

      {renvoyer && (
        <RenvoyerDialog
          proforma={renvoyer}
          onClose={() => setRenvoyer(null)}
        />
      )}
      {renvoyerBcaState && (
        <RenvoyerBcaDialog
          bca={renvoyerBcaState}
          onClose={() => setRvBca(null)}
        />
      )}
    </>
  );
};

function BcaValidationCard({
  bca: b, pending, onValider, onRenvoyer,
}: {
  bca: Bca;
  pending:   boolean;
  onValider: () => void;
  onRenvoyer: () => void;
}) {
  const [downloading, setDownloading] = useState(false);
  const ht  = Number(BigInt(b.montantHtCents.toString())) / 100;
  const ttc = Math.round(ht * 1.18);
  const soumiseLe = b.soumiseValidationLe ? new Date(b.soumiseValidationLe) : null;
  const attenteJours = soumiseLe ? Math.floor((Date.now() - soumiseLe.getTime()) / (24 * 3600 * 1000)) : 0;

  const telechargerPdf = async () => {
    setDownloading(true);
    try {
      await achatsApi.telechargerPdf(b.id, b.reference);
      toast.success('PDF téléchargé', b.reference);
    } catch {
      // Toast d'erreur déjà émis par le décodeur blob dans achatsApi
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="ds-panel" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15 }}>
            <Truck size={13} style={{ verticalAlign: 'middle', marginRight: 4, color: '#0F766E' }} />
            {b.reference}
          </div>
          <div style={{ fontSize: 13, color: 'var(--tx-2)' }}>
            {b.fournisseur?.raisonSociale ?? '—'}
            {b.fournisseur?.code && <span style={{ color: 'var(--tx-3)' }}> · {b.fournisseur.code}</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--tx-3)', marginTop: 2, display: 'flex', gap: 8, alignItems: 'center' }}>
            <User size={11} /> Acheteur ID: <code style={{ fontFamily: 'monospace' }}>{b.createdById?.slice(0, 8)}…</code>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          {attenteJours >= 2 && (
            <Badge tone={attenteJours >= 5 ? 'danger' : 'warning'}>
              <AlertTriangle size={11} style={{ verticalAlign: 'middle' }} /> Attente {attenteJours}j
            </Badge>
          )}
        </div>
      </div>

      <div style={{ background: 'var(--bg-2)', borderRadius: 8, padding: 10, marginBottom: 12 }}>
        {(b.lignes ?? []).slice(0, 4).map((l, i) => (
          <div key={l.id ?? i} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 12, padding: '3px 0', fontSize: 13 }}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {l.matierePremiere ? `${l.matierePremiere.code} · ${l.matierePremiere.nom}` : (l.designation ?? '?')}
              {l.nature && (
                <span style={{ fontSize: 11, color: 'var(--tx-3)', marginLeft: 6 }}>
                  {({ matiere_premiere: 'matière', fourniture: 'fourniture', service: 'service', immobilisation: 'patrimoine' } as Record<string, string>)[l.nature] ?? l.nature}
                </span>
              )}
              {l.avertissement && (
                <div style={{ fontSize: 11, color: 'var(--warning, #b45309)', whiteSpace: 'normal' }}>
                  <AlertTriangle size={11} style={{ verticalAlign: 'middle' }} /> {l.avertissement}
                </div>
              )}
            </div>
            <div style={{ fontFamily: 'monospace', color: 'var(--tx-3)' }}>
              {Number(l.quantite).toLocaleString('fr-FR')} {l.matierePremiere?.unite ?? ''}
            </div>
            <div style={{ fontFamily: 'monospace', fontWeight: 700 }}>
              {(Number(BigInt(l.totalLineCents.toString())) / 100).toLocaleString('fr-FR')}
            </div>
          </div>
        ))}
        {(b.lignes?.length ?? 0) > 4 && (
          <div style={{ fontSize: 11, color: 'var(--tx-3)', marginTop: 4 }}>
            + {(b.lignes?.length ?? 0) - 4} autres ligne(s)
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center', marginBottom: 12 }}>
        <div>
          {soumiseLe && (
            <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>
              Soumis le {soumiseLe.toLocaleDateString('fr-FR')} à {soumiseLe.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
          {b.dateLivraisonPrevue && (
            <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>
              Livraison prévue {new Date(b.dateLivraisonPrevue).toLocaleDateString('fr-FR')}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>Total HT</div>
          <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 18 }}>
            {ht.toLocaleString('fr-FR')}
          </div>
          <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>
            TTC {ttc.toLocaleString('fr-FR')} FCFA
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button
          className="ds-btn ds-btn-g"
          onClick={telechargerPdf}
          disabled={downloading}
          title="Ouvrir le PDF pour vérifier le contenu avant validation"
        >
          <Download size={14} /> {downloading ? 'Génération…' : 'Voir PDF'}
        </button>
        <button className="ds-btn ds-btn-g" onClick={onRenvoyer} disabled={pending}>
          <RotateCcw size={14} /> Renvoyer pour correction
        </button>
        <button className="ds-btn ds-btn-p" onClick={onValider} disabled={pending}>
          <CheckCircle2 size={14} /> Valider
        </button>
      </div>
    </div>
  );
}

function RenvoyerBcaDialog({ bca, onClose }: { bca: Bca; onClose: () => void }) {
  const [motif, setMotif] = useState('');
  const renvoyer = useRenvoyerBca();

  const submit = () => {
    if (motif.trim().length < 5) return;
    renvoyer.mutate({ id: bca.id, motif: motif.trim() }, { onSuccess: onClose });
  };

  return (
    <Modal onClose={onClose}>
      <div className="ds-panel" style={{ maxWidth: 480, width: '100%', padding: 20 }}>
        <ModalHeader titre={`Renvoyer ${bca.reference} pour correction`} onClose={onClose} />
        <p style={{ fontSize: 14, color: 'var(--tx-2)', marginBottom: 12 }}>
          Le motif sera affiché à l'acheteur dans sa liste. Il pourra corriger et resoumettre.
        </p>
        <label>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>Motif (min 5 chars)</div>
          <textarea
            value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} autoFocus
            placeholder="ex. Prix ciment > 15% vs contrat cadre — renégocie."
            style={{
              width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2)', borderRadius: 8,
              fontSize: 14, background: 'var(--bg-1)', resize: 'vertical', fontFamily: 'inherit',
            }}
          />
        </label>
        <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button className="ds-btn ds-btn-p" onClick={submit} disabled={motif.trim().length < 5 || renvoyer.isPending}>
            {renvoyer.isPending ? 'Envoi…' : 'Confirmer le renvoi'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SectionHeader({ titre, count, description }: { titre: string; count: number; description: string }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>{titre}</h2>
        <Badge tone="info">{count}</Badge>
      </div>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--tx-3)' }}>{description}</p>
    </div>
  );
}

function ProformaValidationCard({
  proforma: p, pending, onValider, onRenvoyer,
}: {
  proforma: Proforma;
  pending:   boolean;
  onValider: () => void;
  onRenvoyer: () => void;
}) {
  const [downloading, setDownloading] = useState(false);
  const ht = Number(p.montantHtFcfa);
  const ttc = Math.round(ht * 1.18);
  const soumiseLe = p.soumiseValidationLe ? new Date(p.soumiseValidationLe) : null;
  const attenteJours = soumiseLe ? Math.floor((Date.now() - soumiseLe.getTime()) / (24 * 3600 * 1000)) : 0;

  const telechargerPdf = async () => {
    setDownloading(true);
    try {
      await commercialApi.telechargerPdf(p.id, p.reference);
      toast.success('PDF téléchargé', p.reference);
    } catch {
      // Toast d'erreur déjà émis
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="ds-panel" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15 }}>{p.reference}</div>
          <div style={{ fontSize: 13, color: 'var(--tx-2)' }}>
            {p.client?.fullName ?? '—'}
            {p.client?.companyName && <span style={{ color: 'var(--tx-3)' }}> · {p.client.companyName}</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--tx-3)', marginTop: 2, display: 'flex', gap: 8, alignItems: 'center' }}>
            <User size={11} /> Commercial ID: <code style={{ fontFamily: 'monospace' }}>{p.commercialUserId?.slice(0, 8)}…</code>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          {attenteJours >= 2 && (
            <Badge tone={attenteJours >= 5 ? 'danger' : 'warning'}>
              <AlertTriangle size={11} style={{ verticalAlign: 'middle' }} /> Attente {attenteJours}j
            </Badge>
          )}
        </div>
      </div>

      {/* Lignes */}
      <div style={{ background: 'var(--bg-2)', borderRadius: 8, padding: 10, marginBottom: 12 }}>
        {(p.lignes ?? []).slice(0, 4).map((l, i) => (
          <div key={l.id ?? i} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 12, padding: '3px 0', fontSize: 13 }}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {l.produit?.code ?? '?'} · {l.produit?.designation ?? ''}
            </div>
            <div style={{ fontFamily: 'monospace', color: 'var(--tx-3)' }}>
              {Number(l.quantite).toLocaleString('fr-FR')} {l.produit?.uniteVente ?? ''}
            </div>
            <div style={{ fontFamily: 'monospace', fontWeight: 700 }}>
              {Number(l.totalLigneFcfa).toLocaleString('fr-FR')}
            </div>
          </div>
        ))}
        {(p.lignes?.length ?? 0) > 4 && (
          <div style={{ fontSize: 11, color: 'var(--tx-3)', marginTop: 4 }}>
            + {(p.lignes?.length ?? 0) - 4} autres ligne(s)
          </div>
        )}
      </div>

      {/* Totaux + signature */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center', marginBottom: 12 }}>
        <div>
          {p.signatureClientUrl && (
            <a
              href={p.signatureClientUrl} target="_blank" rel="noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
                color: 'var(--pr-1)', textDecoration: 'none',
              }}
            >
              <ExternalLink size={13} /> Voir signature client
            </a>
          )}
          {soumiseLe && (
            <div style={{ fontSize: 11, color: 'var(--tx-3)', marginTop: 2 }}>
              Soumise le {soumiseLe.toLocaleDateString('fr-FR')} à {soumiseLe.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>Total HT</div>
          <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 18 }}>
            {ht.toLocaleString('fr-FR')}
          </div>
          <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>
            TTC {ttc.toLocaleString('fr-FR')} FCFA
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button
          className="ds-btn ds-btn-g"
          onClick={telechargerPdf}
          disabled={downloading}
          title="Ouvrir le PDF pour vérifier le contenu avant validation"
        >
          <Download size={14} /> {downloading ? 'Génération…' : 'Voir PDF'}
        </button>
        <button className="ds-btn ds-btn-g" onClick={onRenvoyer} disabled={pending}>
          <RotateCcw size={14} /> Renvoyer pour correction
        </button>
        <button className="ds-btn ds-btn-p" onClick={onValider} disabled={pending}>
          <CheckCircle2 size={14} /> Valider
        </button>
      </div>
    </div>
  );
}

function RenvoyerDialog({ proforma, onClose }: { proforma: Proforma; onClose: () => void }) {
  const [motif, setMotif] = useState('');
  const renvoyer = useRenvoyerCommande();

  const submit = () => {
    if (motif.trim().length < 5) return;
    renvoyer.mutate({ id: proforma.id, motif: motif.trim() }, { onSuccess: onClose });
  };

  return (
    <Modal onClose={onClose}>
      <div className="ds-panel" style={{ maxWidth: 480, width: '100%', padding: 20 }}>
        <ModalHeader titre={`Renvoyer ${proforma.reference} pour correction`} onClose={onClose} />
        <p style={{ fontSize: 14, color: 'var(--tx-2)', marginBottom: 12 }}>
          Le motif sera affiché au commercial dans sa liste. Il pourra corriger et resoumettre.
        </p>
        <label>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>Motif (min 5 chars)</div>
          <textarea
            value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} autoFocus
            placeholder="ex. Remise incohérente vs tarif cadre client — à ramener à 5%."
            style={{
              width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2)', borderRadius: 8,
              fontSize: 14, background: 'var(--bg-1)', resize: 'vertical', fontFamily: 'inherit',
            }}
          />
        </label>
        <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button className="ds-btn ds-btn-p" onClick={submit} disabled={motif.trim().length < 5 || renvoyer.isPending}>
            {renvoyer.isPending ? 'Envoi…' : 'Confirmer le renvoi'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
